import { UnexpectedArgument } from 'src/app/shared/errors/unexpected-argument.error';
import { find } from 'lodash';
import { JsonMapperService } from './mapper.service';

const mapper = {
    deserialize: <T, K extends keyof T>(field: K, data: T[K], items: T[]): T => {
        const found = find(items, item => item[field] === data);
        if (found !== undefined) {
            return found;
        } else {
            throw new UnexpectedArgument(`no item found with ${String(field)}`, data);
        }
    },

    serialize: <T, K extends keyof T>(field: K, data: T): T[K] => {
        if (data === null || data === undefined) {
            return data as any;
        }
        // If an object is provided, try to read the requested field
        if (typeof data === 'object') {
            if (field in (data as any)) {
                return (data as any)[field];
            }
            throw new UnexpectedArgument(`object does not contain ${String(field)} property`, data as any);
        }
        // Fallback: allow primitives (e.g., an id already) to pass through
        return data as any;
    }
};

export abstract class DataPropertyConverter<T, K extends keyof T> {
    protected mapper = mapper;

    constructor(protected field: K, protected deserializeContextKey?: string) {}

    requiredContextKeys(): void | { serialize?: string[]; deserialize?: string[] } {
        if (this.deserializeContextKey) {
            return { deserialize: [this.deserializeContextKey] };
        }
    }

      deserialize(data: T[K], context: {}): T {
        // Si la valeur est null ou undefined, on la propage telle quelle
        if (data === null || data === undefined) {
          return data as any;
        }

        // Vérifie que le contexte existe
        if (!context) {
          throw Error(`DataPropertyConverter : context key '${this.deserializeContextKey}' is required, context is missing`);
        }

        // Vérifie que la clé de contexte existe
        if (this.deserializeContextKey && !(this.deserializeContextKey in context)) {
          throw Error(
            `DataPropertyConverter : context key '${this.deserializeContextKey}' is required, available context keys : [${Object.keys(context).join(', ')}]`
          );
        }

        const collection = this.deserializeContextKey ? context[this.deserializeContextKey] : undefined;

        try {
          return this.mapper.deserialize(this.field, data, collection);
        } catch (error) {
          console.warn(
            `DataPropertyConverter : unable to resolve '${String(this.field)}' with value '${data}' in context '${this.deserializeContextKey}'`,
            { data, collection }
          );
          return null as any;
        }
      }

    serialize(data: T): T[K] {
        return this.mapper.serialize(this.field, data);
    }
}

export class ArrayClassConverterFactory {
    static build<T>(clazz: new () => T) {
        class Converter {
            deserialize(data: any[], context: any): T[] {
                if (data == null) {
                    return data as any;
                }
                const mapper = new JsonMapperService();
                return (data as any[]).map(item => mapper.deserializeObject(item, clazz, context));
            }
            serialize(data: T[], context: any): any[] {
                if (data == null) {
                    return data as any;
                }
                const mapper = new JsonMapperService();
                return (data as any[]).map(item => mapper.serialize(item, context));
            }
        }
        return Converter;
    }
}

export class ClassConverterFactory {
    static build<T>(clazz: new () => T) {
        class Converter {
            deserialize(data: any, context: any): T {
                if (data == null) {
                    return data as any;
                }
                const mapper = new JsonMapperService();
                return mapper.deserializeObject(data, clazz, context);
            }
            serialize(data: T, context: any): any {
                if (data == null) {
                    return data as any;
                }
                const mapper = new JsonMapperService();
                return mapper.serialize(data, context);
            }
        }
        return Converter;
    }
}
